// Pre-publish validation gate (EDG-1515). Pure — no DB. Runs over the custom proposal's
// staging matrix (a structural superset of ConsolidatedMatrix) BEFORE it is
// materialized into the production bid-tab tables, so an incomplete draft is
// blocked with clear, per-rule errors instead of persisting a broken proposal.

import {
  type ConsolidatedMatrix,
  type ConsolidatedMatrixItem,
  matrixBidders,
} from './persist';

/** Stable machine codes for each block reason (the UI can key messages off these). */
export type PublishValidationCode =
  | 'VENDORS_REQUIRED'
  | 'ITEMS_REQUIRED'
  | 'PRICES_REQUIRED'
  | 'WINNER_REQUIRED'
  | 'OWNER_REQUIRED'
  | 'STATE_REQUIRED'
  | 'ORPHAN_CELLS'
  | 'ITEM_IDENTITY_REQUIRED';

export interface PublishValidationError {
  code: PublishValidationCode;
  message: string;
}

/** Identity the matrix can't carry on its own — resolved by the caller from the
 *  custom proposal (its owner) or the publish request. */
export interface PublishValidationContext {
  /** The owner the published proposal will hang under, or null if unresolved. */
  ownerName: string | null;
  /** The state the proposal belongs to, or null if unset. Owner-scoped
   *  resolution needs it, so publish requires it too. */
  stateId: string | null;
}

export type PublishValidationResult =
  | { ok: true }
  | { ok: false; errors: PublishValidationError[] };

/**
 * The real vendor columns — the printed bidders' `vendorColumnId`s with every
 * engineer's-estimate reference column excluded (`estimates[]`; they are
 * reference columns, not bids). Mirrors the frontend's `biddersOf`, so validation
 * counts exactly the vendors publish will materialize. Returns column IDS.
 */
function estimateColumnIdsOf(matrix: ConsolidatedMatrix): Set<string> {
  return new Set(matrix.estimates ?? []);
}

function biddersOf(matrix: ConsolidatedMatrix): string[] {
  const estimates = estimateColumnIdsOf(matrix);
  return matrixBidders(matrix)
    .map((b) => b.vendorColumnId)
    .filter((id) => !estimates.has(id));
}

/** Every line item, walking the group → schedules[] → items tree with the flat
 *  `group.items` legacy fallback (the same walk `consolidatedMatrixToRows` does). */
function itemsOf(matrix: ConsolidatedMatrix): ConsolidatedMatrixItem[] {
  const out: ConsolidatedMatrixItem[] = [];
  for (const group of matrix.bidGroups ?? []) {
    const groupItems =
      group.schedules && group.schedules.length > 0
        ? group.schedules.flatMap((s) => s.items ?? [])
        : (group.items ?? []);
    out.push(...groupItems);
  }
  return out;
}

/** A cell's extended price — the printed extension, else unit × quantity. A
 *  null/absent quantity defaults to 1 (a lump-sum line), matching what the
 *  materializer applies (`persist.ts` `quantity ?? '1'`) — otherwise the gate
 *  computes 0 for a unit-price-only lump-sum doc and falsely blocks publish with
 *  WINNER_REQUIRED even though the materializer would price it. */
function extendedOf(
  cell: { unitPrice?: number | null; extendedPrice?: number | null },
  quantity: number | null | undefined,
): number | null {
  if (cell.extendedPrice != null && Number.isFinite(cell.extendedPrice)) {
    return cell.extendedPrice;
  }
  if (cell.unitPrice != null && Number.isFinite(cell.unitPrice)) {
    const qty = quantity != null && Number.isFinite(quantity) ? quantity : 1;
    return cell.unitPrice * qty;
  }
  return null;
}

/**
 * Validate a staging matrix for publish. Collects EVERY failing rule (not just
 * the first) so the caller can surface the full block list at once:
 *  - VENDORS_REQUIRED — no real vendor columns to rank.
 *  - ITEMS_REQUIRED   — no line items.
 *  - PRICES_REQUIRED  — items exist but no cell carries a price (empty grid).
 *  - WINNER_REQUIRED  — priced, but no vendor has a positive total, so no low
 *                       bidder can be determined (incoherent totals).
 *  - OWNER_REQUIRED   — no owner resolved to publish under.
 *  - STATE_REQUIRED   — no state set (owner-scoped resolution needs it).
 *  - ORPHAN_CELLS     — a priced cell references a vendor name that isn't a
 *                       real bidder column (unknown/estimate-only vendor).
 *                       `consolidatedMatrixToRows` silently drops these cells;
 *                       this guard exists so publish BLOCKS instead.
 *  - ITEM_IDENTITY_REQUIRED — an item has neither a description nor an item
 *                       number, so it has no usable identity to publish under.
 */
export function validateStagingForPublish(
  matrix: ConsolidatedMatrix,
  ctx: PublishValidationContext,
): PublishValidationResult {
  const errors: PublishValidationError[] = [];
  const bidders = biddersOf(matrix);
  const biddersSet = new Set(bidders);
  const estimateColumnIds = estimateColumnIdsOf(matrix);
  const items = itemsOf(matrix);

  if (bidders.length === 0) {
    errors.push({
      code: 'VENDORS_REQUIRED',
      message: 'At least one vendor is required to publish.',
    });
  }
  if (items.length === 0) {
    errors.push({
      code: 'ITEMS_REQUIRED',
      message: 'At least one line item is required to publish.',
    });
  }

  const hasBlankIdentity = items.some((item) => {
    const description = item.description?.trim();
    const itemNumber =
      item.itemNumber == null ? '' : String(item.itemNumber).trim();
    return !description && !itemNumber;
  });
  if (hasBlankIdentity) {
    errors.push({
      code: 'ITEM_IDENTITY_REQUIRED',
      message:
        'Every line item needs a description or an item number to publish.',
    });
  }

  // A priced cell whose key isn't a known bidder column (and isn't the estimate
  // column, already excluded from `bidders`) references an unknown vendor.
  const hasOrphanCell = items.some((item) =>
    Object.entries(item.bids ?? {}).some(([columnId, cell]) => {
      if (
        !cell ||
        biddersSet.has(columnId) ||
        estimateColumnIds.has(columnId)
      ) {
        return false;
      }
      return cell.unitPrice != null || cell.extendedPrice != null;
    }),
  );
  if (hasOrphanCell) {
    errors.push({
      code: 'ORPHAN_CELLS',
      message:
        'Some priced cells reference a vendor that is not a recognized bidder column.',
    });
  }

  // Per-vendor totals + whether any cell carries a price at all. Only real
  // bidders count — the estimate column is already excluded above.
  const totals = new Map<string, number>();
  let anyPrice = false;
  for (const item of items) {
    for (const bidder of bidders) {
      const cell = item.bids?.[bidder];
      if (!cell) {
        continue;
      }
      if (cell.unitPrice != null || cell.extendedPrice != null) {
        anyPrice = true;
      }
      const ext = extendedOf(cell, item.quantity ?? null);
      if (ext != null) {
        totals.set(bidder, (totals.get(bidder) ?? 0) + ext);
      }
    }
  }

  if (bidders.length > 0 && items.length > 0 && !anyPrice) {
    errors.push({
      code: 'PRICES_REQUIRED',
      message: 'No bid prices found — enter at least one price to publish.',
    });
  }

  const hasWinner = [...totals.values()].some((total) => total > 0);
  if (bidders.length > 0 && items.length > 0 && anyPrice && !hasWinner) {
    errors.push({
      code: 'WINNER_REQUIRED',
      message:
        'Cannot determine a low bidder — no vendor has a positive total.',
    });
  }

  if (!ctx.ownerName || ctx.ownerName.trim().length === 0) {
    errors.push({
      code: 'OWNER_REQUIRED',
      message: 'An owner is required to publish this proposal under.',
    });
  }
  if (!ctx.stateId || ctx.stateId.trim().length === 0) {
    errors.push({
      code: 'STATE_REQUIRED',
      message: 'A state is required to publish this proposal.',
    });
  }

  return errors.length > 0 ? { ok: false, errors } : { ok: true };
}

/** Join the block reasons into one human-readable sentence for a 400 response. */
export function formatPublishValidationErrors(
  errors: PublishValidationError[],
): string {
  return `Cannot publish this draft: ${errors.map((e) => e.message).join(' ')}`;
}
